/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import { getContrastTextColor } from '@sportninja/common/utils/colorContrast'
import type { SportTerminology } from '@sportninja/common/utils/games/sportTerminology'
import type { SubstitutionCardData } from '../../hooks/useScoreboardAnimations'
import { font } from 'src/components/css'

const LEFT_IN = 400
const NAME_DELAY = LEFT_IN // 400
const NAME_IN = 500
const NAME_HOLD = 2600
const NAME_OUT = 400
const LEFT_HOLD = NAME_DELAY + NAME_IN + NAME_HOLD + NAME_OUT // 3500 after left-in ends
const TEXT_FADE_IN_DELAY = 800
const TEXT_FADE_IN = 300
const TEXT_HOLD = 2200
const TEXT_FADE_OUT = 200
const DISMISS_DELAY = LEFT_IN + LEFT_HOLD // 3900
const DISMISS_FADE = 400

const leftExpandKf = keyframes`
  0% { transform: scaleX(0); }
  9.3% { transform: scaleX(1); }
  90.7% { transform: scaleX(1); }
  100% { transform: scaleX(0); }
`

const nameExpandKf = keyframes`
  0% { transform: scaleX(0); }
  14.3% { transform: scaleX(1); }
  88.6% { transform: scaleX(1); }
  100% { transform: scaleX(0); }
`

const nameTextKf = keyframes`
  0% { opacity: 0; }
  11.1% { opacity: 1; }
  92.6% { opacity: 1; }
  100% { opacity: 0; }
`

const dismissKf = keyframes`
  0% { opacity: 1; }
  100% { opacity: 0; }
`

const outerContainer = css`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  padding-bottom: 48px;
  padding-left: 5%;
  z-index: 999;
  pointer-events: none;
  container-type: inline-size;
`

const wrapper = css`
  animation: ${dismissKf} ${DISMISS_FADE}ms ease-in ${DISMISS_DELAY}ms both;
`

const cellSize = 'clamp(53px, 5.4cqw, 66px)'
const fixedW = 'clamp(106px, 10.8cqw, 132px)'
const nameMaxW = 'clamp(600px, 30cqw, 660px)'

const mainRow = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  height: ${cellSize};
`

const fixedLeft = css`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: ${fixedW};
  transform-origin: left center;
  animation: ${leftExpandKf} ${DISMISS_DELAY + DISMISS_FADE}ms ease-out both;
`

const teamLogoCell = css`
  width: ${cellSize};
  height: ${cellSize};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;
`

const teamLogoImg = css`
  width: clamp(35px, 3.5cqw, 43px);
  height: clamp(35px, 3.5cqw, 43px);
  object-fit: contain;
`

const numberCell = css`
  width: ${cellSize};
  height: ${cellSize};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const numberOverlay = css`
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
`

const numberText = css`
  ${font.title};
  font-size: clamp(29px, 3.1cqw, 36px);
  font-weight: 900;
`

const nameArea = css`
  height: ${cellSize};
  width: ${nameMaxW};
  transform-origin: left center;
  overflow: hidden;
  display: flex;
  align-items: center;
  padding: 0 clamp(10px, 1cqw, 12px);
  animation: ${nameExpandKf} ${NAME_IN + NAME_HOLD + NAME_OUT}ms ease-out
    ${NAME_DELAY}ms both;
`

const nameContent = css`
  animation: ${nameTextKf} ${TEXT_FADE_IN + TEXT_HOLD + TEXT_FADE_OUT}ms ease
    ${TEXT_FADE_IN_DELAY}ms both;
`

const playerRowStyle = css`
  display: flex;
  align-items: center;
  gap: 6px;
`

const playerNameText = css`
  ${font.title};
  font-size: clamp(26px, 2.4cqw, 31px);
  font-weight: 800;
  letter-spacing: 1px;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
`

const subOutText = css`
  ${font.body};
  font-size: clamp(12px, 1.7cqw, 15px);
  font-weight: 500;
  letter-spacing: 0.5px;
  margin-top: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: uppercase;
`

const arrowIcon = css`
  font-size: clamp(12px, 1.7cqw, 15px);
  font-weight: 900;
  flex-shrink: 0;
`

const bottomRow = css`
  width: ${fixedW};
  height: clamp(31px, 3cqw, 38px);
  background: ${colors.NEUTRAL_0};
  transform-origin: left center;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-left: clamp(8px, 0.8cqw, 10px);
  gap: clamp(5px, 0.5cqw, 6px);
  animation: ${leftExpandKf} ${DISMISS_DELAY + DISMISS_FADE}ms ease-out both;
`

const labelText = css`
  ${font.body};
  font-size: clamp(13px, 1.4cqw, 17px);
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: ${colors.NEUTRAL_700};
`

const timeLabelText = css`
  ${font.body};
  font-size: clamp(13px, 1.4cqw, 17px);
  font-weight: 700;
  color: ${colors.NEUTRAL_700};
`

interface SubstitutionCardProps {
  data: SubstitutionCardData
  genericSport: boolean
  terminology?: SportTerminology
}

const SubstitutionCard = ({
  data,
  genericSport = false,
  terminology,
}: SubstitutionCardProps) => {
  const textColor = getContrastTextColor(data.teamJerseyColor)

  return (
    <div css={outerContainer}>
      <div key={data.id} css={wrapper}>
        <div css={mainRow}>
          <div css={fixedLeft}>
            <div
              css={css`
                ${teamLogoCell};
                background: ${data.teamJerseyColor};
              `}
            >
              {data.teamLogoUrl ? (
                <img src={data.teamLogoUrl} alt='' css={teamLogoImg} />
              ) : (
                <span
                  css={css`
                    ${font.title};
                    font-weight: 800;
                    color: ${colors.WHITE};
                    font-size: 26px;
                    letter-spacing: 1px;
                    text-transform: uppercase;
                  `}
                >
                  {data.teamAbbrev}
                </span>
              )}
            </div>
            <div
              css={css`
                ${numberCell};
                background: ${data.teamJerseyColor};
              `}
            >
              <div css={numberOverlay}>
                <span
                  css={css`
                    ${numberText};
                    color: ${textColor};
                  `}
                >
                  {data.goalieInNumber ? `#${data.goalieInNumber}` : ''}
                </span>
              </div>
            </div>
          </div>

          <div
            css={css`
              ${nameArea};
              background: ${data.teamJerseyColor};
            `}
          >
            <div css={nameContent}>
              <div css={playerRowStyle}>
                {data.hasGoalieIn ? (
                  <span
                    css={css`
                      ${arrowIcon};
                      color: ${textColor};
                    `}
                  >
                    ▲
                  </span>
                ) : null}
                <span
                  css={css`
                    ${playerNameText};
                    color: ${textColor};
                  `}
                >
                  {data.goalieInName.toUpperCase()}
                </span>
              </div>
              {data.goalieOutName ? (
                <div css={playerRowStyle}>
                  <span
                    css={css`
                      ${arrowIcon};
                      color: ${textColor};
                    `}
                  >
                    ▼
                  </span>
                  <span
                    css={css`
                      ${subOutText};
                      color: ${textColor};
                    `}
                  >
                    {data.goalieOutNumber ? `#${data.goalieOutNumber} ` : ''}
                    {data.goalieOutName}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div css={bottomRow}>
          <span css={labelText}>{terminology?.substitution ?? 'Sub'}</span>
          <span css={timeLabelText}>
            {genericSport ? null : data.clockTime}
          </span>
        </div>
      </div>
    </div>
  )
}

export default SubstitutionCard
